/**
 * DynApp — standalone interactive Flux app viewer.
 *
 * Two-tier access:
 *   - Anyone with the URL can VIEW (WebSocket stream from flux-service, no auth)
 *   - Signed-in users can INTERACT (dispatch intents via REST + Firebase ID token)
 *
 * URL format: dynapp.continua.ai/{sessionId}
 */
import './flux-cns.css';
import { auth, FLUX_API_BASE, googleProvider } from './firebase';
import { onAuthStateChanged, signInWithPopup, signOut, type User } from 'firebase/auth';
import { CNSNode } from './CNSRenderer';
import { useSessionStream } from './useSessionStream';
import { useCallback, useEffect, useMemo, useState } from 'react';

function getSessionId(): string | null {
  const path = window.location.pathname.replace(/^\/+/, '');
  return path || null;
}

export function DynApp() {
  const sessionId = useMemo(getSessionId, []);

  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(false);

  const {
    publicState,
    cnsView,
    title,
    status,
    participants,
    loading,
    error,
  } = useSessionStream(sessionId);

  // Listen for auth state changes.
  useEffect(() => {
    return onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
    });
  }, []);

  const handleSignIn = useCallback(async () => {
    try {
      await signInWithPopup(auth, googleProvider);
      setAuthPrompt(false);
    } catch (e) {
      console.warn('[dynapp] sign-in error:', e);
    }
  }, []);

  const handleSignOut = useCallback(async () => {
    await signOut(auth);
  }, []);

  const handleDispatch = useCallback(
    async (intentName: string, payload: Record<string, unknown>) => {
      if (!sessionId) return;

      // If not signed in, show sign-in prompt instead of dispatching.
      if (!user) {
        setAuthPrompt(true);
        return;
      }

      setDispatching(true);
      try {
        const idToken = await user.getIdToken();
        await fetch(`${FLUX_API_BASE}/api/intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({
            session_id: sessionId,
            intent_name: intentName,
            payload,
          }),
        });
      } catch (e) {
        console.warn('[dynapp] dispatch error:', e);
      } finally {
        setDispatching(false);
      }
    },
    [sessionId, user],
  );

  // Loading state
  if (loading) {
    return (
      <div className="flux-app-screen">
        <div className="flux-app-card">
          <div className="flux-app-loader">
            <div className="flux-app-spinner" />
            <span>Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flux-app-screen">
        <div className="flux-app-card">
          <div className="flux-app-error">
            <div className="flux-app-error-icon">🔗</div>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  const isActive = status === 'SESSION_STATUS_ACTIVE' || status === 'active';

  return (
    <div className="flux-app-screen">
      <div className="flux-app-card">
        {/* Header */}
        <div className="flux-app-header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h1 className="flux-app-title">{title || 'Continua'}</h1>
              <div className="flux-app-meta">
                <span className={`flux-app-status ${isActive ? 'flux-app-status--active' : ''}`} />
                <span>{isActive ? 'Live' : 'Ended'}</span>
                {participants.length > 0 && (
                  <span>
                    · {participants.length} participant{participants.length !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            {/* Auth indicator */}
            {!authLoading && (
              <div style={{ fontSize: 12, color: 'var(--gravity-text-muted)', textAlign: 'right' }}>
                {user ? (
                  <div>
                    <span style={{ color: 'var(--gravity-text-secondary)' }}>
                      {user.displayName ?? user.email ?? 'Signed in'}
                    </span>
                    <button
                      onClick={handleSignOut}
                      style={{
                        marginLeft: 8,
                        background: 'none',
                        border: 'none',
                        color: 'var(--gravity-text-muted)',
                        cursor: 'pointer',
                        fontSize: 11,
                        textDecoration: 'underline',
                      }}
                    >
                      Sign out
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={handleSignIn}
                    style={{
                      background: 'none',
                      border: '1px solid var(--gravity-border-default)',
                      borderRadius: 6,
                      padding: '4px 10px',
                      color: 'var(--gravity-text-secondary)',
                      cursor: 'pointer',
                      fontSize: 12,
                    }}
                  >
                    Sign in to participate
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Auth prompt overlay */}
        {authPrompt && !user && (
          <div
            style={{
              background: 'var(--gravity-surface)',
              border: '1px solid var(--gravity-border-default)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 16,
              textAlign: 'center',
            }}
          >
            <p style={{ margin: '0 0 12px', color: 'var(--gravity-text-secondary)', fontSize: 14 }}>
              Sign in to interact with this app
            </p>
            <button
              onClick={handleSignIn}
              style={{
                background: 'var(--gravity-accent)',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '10px 24px',
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 48 48">
                <path
                  fill="#FFC107"
                  d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
                />
                <path
                  fill="#FF3D00"
                  d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
                />
                <path
                  fill="#4CAF50"
                  d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0124 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
                />
                <path
                  fill="#1976D2"
                  d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 01-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
                />
              </svg>
              Continue with Google
            </button>
            <button
              onClick={() => setAuthPrompt(false)}
              style={{
                display: 'block',
                margin: '12px auto 0',
                background: 'none',
                border: 'none',
                color: 'var(--gravity-text-muted)',
                cursor: 'pointer',
                fontSize: 12,
              }}
            >
              Not now
            </button>
          </div>
        )}

        {/* CNS View */}
        <div className="flux-app-body">
          {cnsView ? (
            <CNSNode
              view={cnsView}
              state={publicState}
              onDispatchIntent={handleDispatch}
              sessionId={sessionId!}
              isDispatching={dispatching}
              participants={participants}
            />
          ) : (
            <div className="flux-app-loader">
              <div className="flux-app-spinner" />
              <span>Waiting for content…</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ marginTop: 'auto', paddingTop: 24, textAlign: 'center' }}>
          <a
            href="https://continua.ai"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 11,
              color: 'var(--gravity-text-faint, #606080)',
              textDecoration: 'none',
            }}
          >
            Powered by Continua
          </a>
        </div>
      </div>
    </div>
  );
}
